import { CartAddEvent } from '@theme/events';

/* FIX: unified toast stack — rich cart + compact wishlist */
const DISMISS_MS = 3500;
const MAX_VISIBLE = 2;
const ADD_SOURCES = new Set(['product-form-component']);
const CART_EVENT = CartAddEvent.eventName;

const HEART_PATH =
  'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z';

/**
 * @typedef {object} ToastOptions
 * @property {string} id
 * @property {'compact' | 'cart-rich'} [variant]
 * @property {string} title
 * @property {string} [subtitle]
 * @property {string} [imageUrl]
 * @property {string} [actionLabel]
 * @property {string} [actionHref]
 * @property {'heart-filled' | 'heart-outline'} [icon]
 */

/**
 * @typedef {object} ToastState
 * @property {HTMLElement} el
 * @property {number | null} hideTimer
 * @property {boolean} hovering
 */

/** @type {Map<string, ToastState>} */
const activeToasts = new Map();

/**
 * @returns {HTMLElement | null}
 */
function getStack() {
  return document.getElementById('site-toast-stack');
}

/**
 * @param {'heart-filled' | 'heart-outline'} variant
 * @returns {HTMLElement}
 */
function createHeartSvg(variant) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('class', 'site-toast__icon-svg');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', HEART_PATH);

  if (variant === 'heart-filled') {
    path.setAttribute('fill', '#F04438');
    path.setAttribute('stroke', 'none');
  } else {
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', '#6b6b6b');
    path.setAttribute('stroke-width', '1.75');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
  }

  svg.appendChild(path);
  return svg;
}

/**
 * @param {'heart-filled' | 'heart-outline'} variant
 * @returns {HTMLElement}
 */
function createHeartBadge(variant) {
  const badge = document.createElement('span');
  badge.className = `site-toast__icon-badge site-toast__icon-badge--${variant}`;
  badge.setAttribute('aria-hidden', 'true');
  badge.appendChild(createHeartSvg(variant));
  return badge;
}

/**
 * @returns {HTMLElement}
 */
function createCheckIcon() {
  const check = document.createElement('span');
  check.className = 'site-toast__check';
  check.setAttribute('aria-hidden', 'true');

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('class', 'site-toast__check-svg');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M6 12.5l4 4 8-9');
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', '#168F52');
  path.setAttribute('stroke-width', '2');
  path.setAttribute('stroke-linecap', 'round');
  path.setAttribute('stroke-linejoin', 'round');
  path.setAttribute('vector-effect', 'non-scaling-stroke');
  path.setAttribute('transform', 'translate(12 12) scale(1.5) translate(-12 -12)');

  svg.appendChild(path);
  check.appendChild(svg);
  return check;
}

/**
 * @param {ToastOptions} options
 * @returns {DocumentFragment}
 */
function buildCompactContent(options) {
  const { title, actionLabel, actionHref, icon } = options;
  const frag = document.createDocumentFragment();

  const text = document.createElement('div');
  text.className = 'site-toast__text';

  const titleRow = document.createElement('div');
  titleRow.className = 'site-toast__title-row';

  if (icon) {
    titleRow.appendChild(createHeartBadge(icon));
  }

  const titleEl = document.createElement('span');
  titleEl.className = 'site-toast__title';
  titleEl.textContent = title;
  titleRow.appendChild(titleEl);
  text.appendChild(titleRow);

  const actionSlot = document.createElement('div');
  actionSlot.className = 'site-toast__action-slot';
  if (actionLabel && actionHref) {
    const link = document.createElement('a');
    link.className = 'site-toast__link';
    link.href = actionHref;
    link.textContent = actionLabel;
    actionSlot.appendChild(link);
  }
  text.appendChild(actionSlot);

  frag.appendChild(text);
  return frag;
}

/**
 * @param {ToastOptions} options
 * @returns {DocumentFragment}
 */
function buildCartRichContent(options) {
  const { title, subtitle, imageUrl, actionLabel, actionHref } = options;
  const frag = document.createDocumentFragment();

  const body = document.createElement('div');
  body.className = 'site-toast__body';

  if (imageUrl) {
    const img = document.createElement('img');
    img.className = 'site-toast__thumb';
    img.src = imageUrl;
    img.alt = '';
    img.width = 48;
    img.height = 48;
    img.loading = 'lazy';
    img.decoding = 'async';
    body.appendChild(img);
  } else {
    const placeholder = document.createElement('span');
    placeholder.className = 'site-toast__thumb site-toast__thumb--placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    body.appendChild(placeholder);
  }

  const content = document.createElement('div');
  content.className = 'site-toast__content';

  const heading = document.createElement('div');
  heading.className = 'site-toast__heading';
  heading.appendChild(createCheckIcon());

  const headingText = document.createElement('span');
  headingText.textContent = title;
  heading.appendChild(headingText);
  content.appendChild(heading);

  if (subtitle) {
    const sub = document.createElement('p');
    sub.className = 'site-toast__subtitle';
    sub.textContent = subtitle;
    content.appendChild(sub);
  }

  body.appendChild(content);

  if (actionLabel && actionHref) {
    const btn = document.createElement('a');
    btn.className = 'site-toast__btn';
    btn.href = actionHref;
    btn.textContent = actionLabel;
    body.appendChild(btn);
  }

  frag.appendChild(body);
  return frag;
}

/**
 * @param {HTMLElement} el
 * @param {ToastOptions} options
 */
function renderToastContent(el, options) {
  const variant = options.variant || 'compact';
  el.className = `site-toast site-toast--${variant}`;
  el.replaceChildren();

  const content =
    variant === 'cart-rich' ? buildCartRichContent(options) : buildCompactContent(options);
  el.appendChild(content);
}

/**
 * @param {string} id
 */
function dismissToast(id) {
  const state = activeToasts.get(id);
  if (!state) return;

  activeToasts.delete(id);

  if (state.hideTimer) {
    clearTimeout(state.hideTimer);
    state.hideTimer = null;
  }

  const { el } = state;
  el.classList.remove('is-visible');
  el.classList.add('is-exiting');

  const remove = () => {
    el.remove();
  };

  const onEnd = (event) => {
    if (event.target !== el) return;
    remove();
  };

  el.addEventListener('transitionend', onEnd, { once: true });
  window.setTimeout(remove, 280);
}

/**
 * @param {string} id
 */
function scheduleHide(id) {
  const state = activeToasts.get(id);
  if (!state) return;

  if (state.hideTimer) clearTimeout(state.hideTimer);

  state.hideTimer = window.setTimeout(() => {
    if (state.hovering) return;
    dismissToast(id);
    state.hideTimer = null;
  }, DISMISS_MS);
}

/**
 * @param {ToastOptions} options
 */
function showToast(options) {
  const stack = getStack();
  if (!stack) return;

  const existing = activeToasts.get(options.id);
  if (existing) {
    renderToastContent(existing.el, options);
    existing.el.classList.remove('is-exiting');
    existing.el.classList.add('is-visible');
    existing.hovering = false;
    scheduleHide(options.id);
    return;
  }

  if (activeToasts.size >= MAX_VISIBLE) {
    const oldestId = activeToasts.keys().next().value;
    if (oldestId) dismissToast(oldestId);
  }

  const el = document.createElement('div');
  el.setAttribute('role', 'status');
  el.setAttribute('aria-atomic', 'true');
  renderToastContent(el, options);
  stack.appendChild(el);

  const state = { el, hideTimer: null, hovering: false };
  activeToasts.set(options.id, state);

  el.addEventListener('mouseenter', () => {
    state.hovering = true;
    if (state.hideTimer) {
      clearTimeout(state.hideTimer);
      state.hideTimer = null;
    }
  });

  el.addEventListener('mouseleave', () => {
    state.hovering = false;
    scheduleHide(options.id);
  });

  el.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.site-toast__btn, .site-toast__link')) return;
    dismissToast(options.id);
  });

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      el.classList.add('is-visible');
    });
  });

  scheduleHide(options.id);
}

/**
 * @param {Event} event
 * @returns {boolean}
 */
function shouldShowCartToast(event) {
  const data = /** @type {{ data?: { didError?: boolean; source?: string } }} */ (event).detail?.data;
  if (!data || data.didError) return false;
  const source = data.source;
  return typeof source === 'string' && ADD_SOURCES.has(source);
}

/**
 * @param {Event} event
 */
function handleCartUpdate(event) {
  if (!shouldShowCartToast(event)) return;

  const stack = getStack();
  const cartUrl = stack?.dataset.cartUrl || '/cart';
  const data = /** @type {{ productTitle?: string; productImageUrl?: string }} */ (event.detail?.data);

  showToast({
    id: 'cart',
    variant: 'cart-rich',
    title: 'Sepete Eklendi',
    subtitle: data.productTitle || '',
    imageUrl: data.productImageUrl || '',
    actionLabel: 'Sepete Git',
    actionHref: cartUrl,
  });
}

function initSiteToast() {
  const stack = getStack();
  if (!stack || stack.dataset.siteToastInit === 'true') return;

  stack.dataset.siteToastInit = 'true';
  document.addEventListener(CART_EVENT, handleCartUpdate);

  window.SiteToast = {
    show: showToast,
    dismiss: dismissToast,
  };
}

function bootSiteToast() {
  initSiteToast();
  if (!getStack()?.dataset.siteToastInit) {
    document.addEventListener('DOMContentLoaded', initSiteToast, { once: true });
  }
}

bootSiteToast();
